import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import CommandCenter from "@/pages/CommandCenter";
import RTOExplorer from "@/pages/RTOExplorer";
import RTODetail from "@/pages/RTODetail";
import FocusMarket from "@/pages/FocusMarket";
import BrandWars from "@/pages/BrandWars";
import Uploads from "@/pages/Uploads";
import Admin from "@/pages/Admin";
import League from "@/pages/League";
import Branches from "@/pages/Branches";
import DpcCommand from "@/pages/DpcCommand";
import DpcOverview from "@/pages/DpcOverview";
import DpcDealer from "@/pages/DpcDealer";
import DpcPlans from "@/pages/DpcPlans";
import DpcPlanEntry from "@/pages/DpcPlanEntry";
import DpcUpload from "@/pages/DpcUpload";
import DpcLeague from "@/pages/DpcLeague";
import ServiceHome from "@/pages/ServiceHome";
import ServiceOverview from "@/pages/ServiceOverview";
import ServiceUpload from "@/pages/ServiceUpload";
import ServiceBenchmark from "@/pages/ServiceBenchmark";
import ServiceWorkshop, { WorkshopEntry } from "@/pages/ServiceWorkshop";
import ServiceOpenRos from "@/pages/ServiceOpenRos";
import ServiceManpower from "@/pages/ServiceManpower";
import ServiceLeaderboard from "@/pages/ServiceLeaderboard";
import ServiceTargets from "@/pages/ServiceTargets";
import StockCommand from "@/pages/StockCommand";
import StockBranch, { StockBranchEntry } from "@/pages/StockBranch";
import StockLocator from "@/pages/StockLocator";
import StockVelocity from "@/pages/StockVelocity";
import StockUnit from "@/pages/StockUnit";
import StockUpload from "@/pages/StockUpload";
import StockBanks from "@/pages/StockBanks";
import { roleLabel } from "@/lib/utils-msip";
import { visibleMods, homePath, canUploadStock, canUploadRetail } from "@/lib/nav";
import { ChangePasswordButton } from "@/components/ChangePasswordDialog";
import { FreshnessFooter } from "@/components/FreshnessFooter";

function MiniShell({ subtitle, children }) {
  const { user, logout } = useApp();
  return (
    <div>
      <header className="sticky top-0 z-40 border-b bg-card flex items-center gap-3 px-5 py-3">
        <div className="font-heading font-extrabold text-lg">Incheon Kia <span style={{ color: "#BB162B" }}>MIS</span></div>
        <span className="text-xs text-muted-foreground">{subtitle}</span>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span className="hidden sm:inline rounded bg-secondary px-2 py-0.5" data-testid="shell-role">{roleLabel(user)}</span>
          <span data-testid="uploader-email">{user?.email}</span>
          <ChangePasswordButton variant="text" />
          <button data-testid="uploader-logout-btn" onClick={logout} className="rounded border px-2.5 py-1 hover:bg-secondary transition-colors duration-200">Log out</button>
        </div>
      </header>
      <div className="px-5 py-1.5 border-b bg-secondary/40"><FreshnessFooter variant="inline" /></div>
      <main className="p-4 lg:p-6">{children}</main>
    </div>
  );
}

function HomeRedirect() {
  const { user } = useApp();
  return <Navigate to={homePath(user)} replace />;
}

function UploaderShell() {
  const [view, setView] = useState("market");
  return (
    <MiniShell subtitle="Data upload portal">
      <div className="flex items-center rounded-md border overflow-hidden text-xs w-fit mb-4">
        {[{ v: "market", l: "Market data" }, { v: "dpc", l: "DPC achievement" }, { v: "service", l: "Service DMS" }, { v: "stock", l: "Stock dump" }].map(({ v, l }) => (
          <button
            key={v}
            data-testid={`uploader-tab-${v}`}
            onClick={() => setView(v)}
            className={`px-3 py-1.5 font-medium transition-colors duration-200 ${view === v ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`}
          >
            {l}
          </button>
        ))}
      </div>
      {view === "market" ? <Uploads /> : view === "dpc" ? <DpcUpload /> : view === "stock" ? <StockUpload /> : <ServiceUpload />}
    </MiniShell>
  );
}

function Protected({ children }) {
  const { user } = useApp();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "uploader") return <UploaderShell />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useApp();
  if (user && user !== false && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function Allow({ when, children }) {
  const { user } = useApp();
  if (user && user !== false && !when(user)) return <Navigate to={homePath(user)} replace />;
  return children;
}

function Gate({ mod, children }) {
  const { user } = useApp();
  if (user && user !== false && !visibleMods(user).has(mod)) return <Navigate to={homePath(user)} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route index element={<Gate mod="mis"><CommandCenter /></Gate>} />
              <Route path="rtos" element={<Gate mod="mis"><RTOExplorer /></Gate>} />
              <Route path="rto/:code" element={<Gate mod="mis"><RTODetail /></Gate>} />
              <Route path="focus" element={<Gate mod="mis"><FocusMarket /></Gate>} />
              <Route path="brandwars" element={<Gate mod="mis"><BrandWars /></Gate>} />
              <Route path="league" element={<Gate mod="common"><League /></Gate>} />
              <Route path="branches" element={<Gate mod="common"><Branches /></Gate>} />
              <Route path="dpc" element={<Gate mod="dpc"><DpcCommand /></Gate>} />
              <Route path="dpc/overview" element={<Gate mod="dpc"><DpcOverview /></Gate>} />
              <Route path="dpc/plans" element={<Gate mod="dpc"><DpcPlans /></Gate>} />
              <Route path="dpc/league" element={<Gate mod="dpc"><DpcLeague /></Gate>} />
              <Route path="dpc/plan/:code" element={<Gate mod="dpc"><DpcPlanEntry /></Gate>} />
              <Route path="dpc/dealer/:code" element={<Gate mod="dpc"><DpcDealer /></Gate>} />
              <Route path="dpc/upload" element={<AdminOnly><DpcUpload /></AdminOnly>} />
              <Route path="stock" element={<Gate mod="stock"><StockCommand /></Gate>} />
              <Route path="stock/branch" element={<Gate mod="stock"><StockBranchEntry /></Gate>} />
              <Route path="stock/branch/:code" element={<Gate mod="stock"><StockBranch /></Gate>} />
              <Route path="stock/locator" element={<Gate mod="stock"><StockLocator /></Gate>} />
              <Route path="stock/velocity" element={<Gate mod="stock"><StockVelocity /></Gate>} />
              <Route path="stock/unit/:chassis" element={<Gate mod="stock"><StockUnit /></Gate>} />
              <Route path="stock/banks" element={<Gate mod="stock"><StockBanks /></Gate>} />
              <Route path="stock/upload" element={<Allow when={(u) => canUploadStock(u) || canUploadRetail(u)}><StockUpload /></Allow>} />
              <Route path="service" element={<Gate mod="service"><ServiceHome /></Gate>} />
              <Route path="service/overview" element={<Gate mod="service"><ServiceOverview /></Gate>} />
              <Route path="service/benchmark" element={<Gate mod="service"><ServiceBenchmark /></Gate>} />
              <Route path="service/workshop" element={<Gate mod="service"><WorkshopEntry /></Gate>} />
              <Route path="service/workshop/:code" element={<Gate mod="service"><ServiceWorkshop /></Gate>} />
              <Route path="service/open-ros" element={<Gate mod="service"><ServiceOpenRos /></Gate>} />
              <Route path="service/manpower" element={<Gate mod="service"><ServiceManpower /></Gate>} />
              <Route path="service/leaderboard" element={<Gate mod="service"><ServiceLeaderboard /></Gate>} />
              <Route path="service/targets" element={<Gate mod="service"><ServiceTargets /></Gate>} />
              <Route path="service/upload" element={<AdminOnly><ServiceUpload /></AdminOnly>} />
              <Route path="uploads" element={<AdminOnly><Uploads /></AdminOnly>} />
              <Route path="admin" element={<AdminOnly><Admin /></AdminOnly>} />
              <Route path="*" element={<HomeRedirect />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors offset={64} />
      </AppProvider>
    </div>
  );
}

export default App;
